import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Avatar } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { Plus, Clock, Settings, Trash2, PlusCircle, Building2, Package } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

import { Integration, IntegrationBase } from "../types/integration";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { IntegrationService } from "../services/integrationsService";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { IntegrationsForm } from "./IntegrationsForm";
import IntegrationsSelector from "./IntegrationsSelector";
import { IntegrationsDelete } from "./IntegrationsDelete";
import IntegrationsExamples from "./IntegrationsExamples";
import { AddMcpServerDialog } from "./AddMcpServerDialog";
import { instanceKind, templateKind } from "../utils/integrationKind";
import { integrationPath } from "../utils/integrationTabs";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";

/** Status que a lista de Tools mostra: as ativas e as que aguardam conexão (AB#87477). */
const LISTED_STATUSES = new Set(['active', 'pending']);
function isListedStatus(status?: string | null): boolean {
  return LISTED_STATUSES.has((status ?? '').toLowerCase());
}
function isAwaitingConnection(status?: string | null): boolean {
  return (status ?? '').toLowerCase() === 'pending';
}

/**
 * Organização alvo, quando NÃO é a ativa da sessão (AB#85605).
 *
 * A mesma tela serve os provedores de IA da plataforma, que pertencem à organização da
 * plataforma — o superadmin os administra em escopo de plataforma, onde não há organização
 * ativa e, até este item, esta tela não listava nada.
 */
interface IntegrationsBaseProps {
  organizationId?: string;
}

const IntegrationsBase = ({ organizationId: targetOrganizationId }: IntegrationsBaseProps = {}) => {
  const { t, i18n } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { organizationId } = useViewScope();
  const navigate = useNavigate();

  // Estados para adicionar integração
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Integration | null>(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  // Servidor MCP (AB#87477): template `mcp` do catálogo (URL travada) ou "Adicionar novo".
  const [showMcpDialog, setShowMcpDialog] = useState(false);
  const [mcpTemplate, setMcpTemplate] = useState<Integration | null>(null);
  
  // Estados para edição e remoção de integração
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationBase | null>(null);

  // Query para buscar templates (integrations) da tabela integrations
  const { data: templates = [] } = useQuery({
    queryKey: ["integration-templates"],
    queryFn: () => IntegrationService.getAll(),
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Query para buscar integrações base (já configuradas) — com product_id para info de escopo.
  // Em Visão da organização (sem workspace), filtra apenas integrações globais —
  // as locais (vinculadas a workspaces) só aparecem quando há um workspace selecionado.
  const effectiveOrganizationId =
    targetOrganizationId || currentWorkspace?.organization_id || organizationId;
  const isOrgView = !currentWorkspace?.id;
  const { data: integrationsBase = [], isLoading } = useQuery({
    queryKey: ["integrations-base", effectiveOrganizationId, currentWorkspace?.id, isOrgView ? 'global' : 'all'],
    queryFn: async () => {
      if (!effectiveOrganizationId) {
        throw new Error('Organization ID is required');
      }

      // Ativas e as que aguardam conexão (AB#87477): uma instalação OAuth nasce `pending`
      // até a autorização voltar — escondê-la deixaria o admin sem como concluir.
      const all = await IntegrationBaseService.getAll({
        ...(targetOrganizationId ? { organization_id: targetOrganizationId } : {}),
        ...(currentWorkspace?.id ? { product_id: currentWorkspace.id } : { scope: 'global' })
      });
      const data = all.filter((integration) => isListedStatus(integration.status));

      // Para integrações base, não precisamos fazer mapeamento de logos fixas
      // pois elas já vêm com os dados do template
      return data.sort((a, b) => {
        // Ordenar por status (ativas primeiro) e depois por label
        if (a.status?.toLowerCase() === 'active' && b.status?.toLowerCase() !== 'active') return -1;
        if (a.status?.toLowerCase() !== 'active' && b.status?.toLowerCase() === 'active') return 1;
        return a.label.localeCompare(b.label);
      });
    },
    enabled: !!effectiveOrganizationId,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Função para selecionar template de integração
  const handleSelectTemplate = (template: Integration) => {
    setShowAddPanel(false);
    // Template `mcp` instala pelo diálogo do servidor, com a URL dele travada; o
    // formulário de settings/credenciais é só do conector `api` (AB#87477).
    if (templateKind(template) === 'mcp') {
      setMcpTemplate(template);
      setShowMcpDialog(true);
      return;
    }
    setSelectedTemplate(template);
    setShowCredentialsModal(true);
  };

  const handleAddMcpServer = () => {
    setShowAddPanel(false);
    setMcpTemplate(null);
    setShowMcpDialog(true);
  };
  
  const handleConnectionSuccess = () => {
    setSelectedTemplate(null);
  };

  // Funções para editar e remover integrações
  const handleEditIntegration = async (integration: IntegrationBase) => {
    // A edição virou página (AB#87542). Os provedores de IA da PLATAFORMA continuam no
    // modal: a página vive em `/integrations/:id`, que exige organização ativa, e o
    // superadmin os administra em escopo de plataforma.
    if (!targetOrganizationId) {
      navigate(integrationPath(integration.id));
      return;
    }
    // Buscar dados frescos + template separadamente
    try {
      // 1. Buscar integration_base atualizada
      const freshIntegration = await IntegrationBaseService.getById(
        integration.id,
        targetOrganizationId,
      );
      
      // 2. Buscar template da tabela integrations usando integration_id
      const template = await IntegrationService.getById(freshIntegration.integration_id);
      
      // 3. Combinar template com integration_base
      const integrationWithTemplate: IntegrationBase = {
        ...freshIntegration,
        template: template
      };
      
      setSelectedIntegration(integrationWithTemplate);
      setShowEditModal(true);
    } catch {
      // Fallback deliberado, nao erro engolido: o cache ja tem o suficiente para abrir a
      // edicao, entao a tela abre com ele em vez de barrar quem so quis editar. O detalhe
      // da falha nao vai para o console — nao ha nada que o usuario possa fazer com ele.
      setSelectedIntegration(integration);
      setShowEditModal(true);
    }
  };

  const handleDeleteIntegration = (integration: IntegrationBase) => {
    setSelectedIntegration(integration);
    setShowDeleteDialog(true);
  };



  return (
    <>
      <div>
        <div className="container mx-auto py-4 md:py-8">
        {/* Header Section */}
        <div className="text-center mb-6 md:mb-8 px-4">
          <IntegrationsExamples onAddClick={() => setShowAddPanel(true)} />
        </div>

        {/* Content Section */}
        <div className="max-w-5xl mx-auto w-full space-y-4 md:space-y-6">

          {/* Lista de integrações já configuradas */}
          {isLoading ? (
            <div className="grid gap-3 md:gap-4">
              {/* Skeleton loading cards */}
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="px-4 md:px-6 py-3 md:py-4">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className="h-10 w-10 md:h-12 md:w-12 bg-muted rounded-lg"></div>
                        <div className="space-y-2">
                          <div className="h-4 w-32 bg-muted rounded"></div>
                          <div className="h-3 w-48 bg-muted rounded"></div>
                        </div>
                      </div>
                      <div className="h-6 w-20 bg-muted rounded"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : integrationsBase.length > 0 ? (
            <>
              {/* Call-to-action centralizado e moderno */}
              <div className="flex justify-center items-center py-6 mb-6">
                <div className="relative">
                  {/* Gradient background decorativo */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-full blur-xl scale-110"></div>
                  <Button
                    onClick={() => setShowAddPanel(true)}
                    className="relative flex items-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    size="lg"
                    data-tour="add-integration-button"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">{t("integrations.addIntegration")}</span>
                  </Button>
                </div>
              </div>

              {/* Integrações do Produto (Local) */}
              {integrationsBase.filter(i => i.scope === 'local').length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 px-1">
                    <Package className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">
                      {t('integrations.scope.sectionWorkspace', 'Do Workspace')}
                    </h3>
                    <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4 ml-1">
                      {integrationsBase.filter(i => i.scope === 'local').length}
                    </Badge>
                  </div>
                  <div className="grid gap-3 md:gap-4">
                    {integrationsBase.filter(i => i.scope === 'local').map((integrationBase) => {
                      const template = templates.find(t => t.id === integrationBase.integration_id);
                      const isBlocked = template?.settings?.block === true;
                      return (
                        <Card key={integrationBase.id} className="transition-all duration-200 hover:shadow-md">
                          <CardContent className="px-4 md:px-6 py-3 md:py-4">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-3 md:gap-4">
                                <Avatar className="h-10 w-10 md:h-12 md:w-12 rounded-lg bg-primary/10">
                                  {integrationBase.logo_url && (
                                    <img
                                      src={sameOriginProxyUrl(integrationBase.logo_url)}
                                      alt={integrationBase.label || 'Integration logo'}
                                      className="aspect-square h-full w-full object-contain p-1.5"
                                    />
                                  )}
                                </Avatar>
                                <div className="flex flex-col items-start text-left">
                                  <span className="text-sm md:text-base font-semibold truncate max-w-[150px] md:max-w-[200px] lg:max-w-none">
                                    {integrationBase.title || integrationBase.label}
                                  </span>
                                  {integrationBase.title && (
                                    <span className="text-xs text-muted-foreground">
                                      {integrationBase.label}
                                    </span>
                                  )}
                                  {instanceKind(integrationBase) === 'mcp' && (
                                    <Badge variant="outline" className="mt-1 text-[10px] px-1.5 py-0">
                                      {t('integrations.kind.mcp')}
                                    </Badge>
                                  )}
                                  {isAwaitingConnection(integrationBase.status) && (
                                    <button
                                      type="button"
                                      onClick={() => navigate(integrationPath(integrationBase.id, 'connection'))}
                                      className="mt-1"
                                    >
                                      <Badge variant="warning" className="text-[10px] px-1.5 py-0">
                                        {t('integrations.status.awaitingConnection')}
                                      </Badge>
                                    </button>
                                  )}
                                  <span className="text-xs md:text-sm text-muted-foreground line-clamp-1 max-w-[200px] md:max-w-[300px] lg:max-w-[400px]">
                                    {getIntegrationDescription(integrationBase, i18n.language) || t("common.noDescription")}
                                  </span>
                                  {integrationBase.last_validated_at && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-xs text-muted-foreground">
                                        {t("integrations.lastValidated")}: {new Date(integrationBase.last_validated_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 md:gap-3">
                                {integrationBase.is_encrypted && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t("integrations.encrypted")}
                                  </Badge>
                                )}
                                {!isBlocked && (
                                  <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => handleEditIntegration(integrationBase)} className="h-8 w-8 p-0 rounded-full" title={t('integrations.actions.configure')}>
                                      <Settings className="h-4 w-4" />
                                      <span className="sr-only">{t('integrations.actions.configure')}</span>
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleDeleteIntegration(integrationBase)} className="h-8 w-8 p-0 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10" title={t('integrations.actions.remove')}>
                                      <Trash2 className="h-4 w-4" />
                                      <span className="sr-only">{t('integrations.actions.remove')}</span>
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Integrações da Organização (Global) */}
              {integrationsBase.filter(i => i.scope !== 'local').length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 px-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">
                      {t('integrations.scope.sectionOrganization', 'De toda a organização')}
                    </h3>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-1">
                      {integrationsBase.filter(i => i.scope !== 'local').length}
                    </Badge>
                  </div>
                  <div className="grid gap-3 md:gap-4">
                    {integrationsBase.filter(i => i.scope !== 'local').map((integrationBase) => {
                      const template = templates.find(t => t.id === integrationBase.integration_id);
                      const isBlocked = template?.settings?.block === true;
                      return (
                        <Card key={integrationBase.id} className="transition-all duration-200 hover:shadow-md">
                          <CardContent className="px-4 md:px-6 py-3 md:py-4">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-3 md:gap-4">
                                <Avatar className="h-10 w-10 md:h-12 md:w-12 rounded-lg bg-primary/10">
                                  {integrationBase.logo_url && (
                                    <img
                                      src={sameOriginProxyUrl(integrationBase.logo_url)}
                                      alt={integrationBase.label || 'Integration logo'}
                                      className="aspect-square h-full w-full object-contain p-1.5"
                                    />
                                  )}
                                </Avatar>
                                <div className="flex flex-col items-start text-left">
                                  <span className="text-sm md:text-base font-semibold truncate max-w-[150px] md:max-w-[200px] lg:max-w-none">
                                    {integrationBase.title || integrationBase.label}
                                  </span>
                                  {integrationBase.title && (
                                    <span className="text-xs text-muted-foreground">
                                      {integrationBase.label}
                                    </span>
                                  )}
                                  {instanceKind(integrationBase) === 'mcp' && (
                                    <Badge variant="outline" className="mt-1 text-[10px] px-1.5 py-0">
                                      {t('integrations.kind.mcp')}
                                    </Badge>
                                  )}
                                  {isAwaitingConnection(integrationBase.status) && (
                                    <button
                                      type="button"
                                      onClick={() => navigate(integrationPath(integrationBase.id, 'connection'))}
                                      className="mt-1"
                                    >
                                      <Badge variant="warning" className="text-[10px] px-1.5 py-0">
                                        {t('integrations.status.awaitingConnection')}
                                      </Badge>
                                    </button>
                                  )}
                                  <span className="text-xs md:text-sm text-muted-foreground line-clamp-1 max-w-[200px] md:max-w-[300px] lg:max-w-[400px]">
                                    {getIntegrationDescription(integrationBase, i18n.language) || t("common.noDescription")}
                                  </span>
                                  {integrationBase.last_validated_at && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-xs text-muted-foreground">
                                        {t("integrations.lastValidated")}: {new Date(integrationBase.last_validated_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 md:gap-3">
                                {integrationBase.is_encrypted && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t("integrations.encrypted")}
                                  </Badge>
                                )}
                                {!isBlocked && (
                                  <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => handleEditIntegration(integrationBase)} className="h-8 w-8 p-0 rounded-full" title="Configurar">
                                      <Settings className="h-4 w-4" />
                                      <span className="sr-only">Configurar</span>
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleDeleteIntegration(integrationBase)} className="h-8 w-8 p-0 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10" title="Remover">
                                      <Trash2 className="h-4 w-4" />
                                      <span className="sr-only">Remover</span>
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
        ) : (
          // Estado vazio moderno - nenhuma integração configurada
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="relative mb-8">
              {/* Círculo decorativo de fundo */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/5 rounded-full blur-3xl scale-150"></div>
              <div className="relative bg-gradient-to-br from-primary/20 to-primary/10 p-6 rounded-2xl">
                <PlusCircle className="h-12 w-12 text-primary mx-auto" />
              </div>
            </div>
            
            <div className="space-y-4 mb-8">
              <h3 className="text-xl font-semibold text-foreground">{t("integrations.empty.title")}</h3>
              <p className="text-muted-foreground max-w-md leading-relaxed">
                {t("integrations.empty.description")}
              </p>
            </div>
            
            <Button
              onClick={() => setShowAddPanel(true)}
              className="flex items-center gap-2 h-11 px-6 rounded-lg bg-primary hover:bg-primary/90 transition-colors"
              size="lg"
            >
              <Plus className="h-4 w-4" />
              {t("integrations.add")}
            </Button>
          </div>
        )}
        </div>
        </div>
      </div>
      
      {/* Flyout para seleção de templates */}
      <IntegrationsSelector
        isOpen={showAddPanel}
        onClose={() => setShowAddPanel(false)}
        onSelectTemplate={handleSelectTemplate}
        onAddMcpServer={handleAddMcpServer}
      />

      {/* Servidor MCP: do catálogo ou novo (AB#87477) */}
      <AddMcpServerDialog
        isOpen={showMcpDialog}
        onOpenChange={setShowMcpDialog}
        template={mcpTemplate}
        organizationId={targetOrganizationId}
        // Na plataforma não há página para abrir (ver handleEditIntegration): só a lista recarrega.
        onCreated={targetOrganizationId ? () => setMcpTemplate(null) : undefined}
      />
      
      {/* Modal de Conexão para Adição */}
      <IntegrationsForm
        isOpen={showCredentialsModal}
        onOpenChange={setShowCredentialsModal}
        selectedTemplate={selectedTemplate}
        onSuccess={handleConnectionSuccess}
        organizationId={targetOrganizationId}
      />
      
      {/* Modal de Edição — só para a plataforma; as demais abrem a página (AB#87542) */}
      <IntegrationsForm
        isOpen={showEditModal}
        onOpenChange={setShowEditModal}
        selectedTemplate={selectedIntegration?.template || null}
        selectedIntegration={selectedIntegration}
        mode="edit"
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedIntegration(null);
        }}
        organizationId={targetOrganizationId}
      />
      
      {/* Dialog de Confirmação de Remoção */}
      <IntegrationsDelete
        isOpen={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        integration={selectedIntegration}
        organizationId={targetOrganizationId}
      />
    </>
  );
};

export default IntegrationsBase;
